import { LegalLayout } from "@/components/legal/LegalLayout";

export function CookiePage() {
  return (
    <LegalLayout title="Cookie Policy" lastUpdated="February 14, 2026">
      <section>
        <h2 id="what-are-cookies">1. What Are Cookies</h2>
        <p>
          Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and supply information to the owners of the site.
        </p>
        <p>
          Cookies may be set by the website you are visiting ("first-party cookies") or by third parties whose services the website uses ("third-party cookies"). Cookies can remain on your device for different periods of time: "session cookies" are deleted when you close your browser, while "persistent cookies" remain on your device until they expire or are manually deleted.
        </p>
      </section>

      <section>
        <h2 id="how-we-use">2. How We Use Cookies</h2>
        <p>
          Rayloop AI ("the Service") uses cookies and similar technologies for several purposes, including:
        </p>
        <ul>
          <li>Authenticating your identity and keeping you signed in as you navigate between pages.</li>
          <li>Remembering your preferences and settings, such as your preferred theme (light or dark mode) and canvas layout configurations.</li>
          <li>Understanding how you use the Service so we can improve its performance and usability.</li>
          <li>Measuring the effectiveness of our marketing campaigns and communications.</li>
          <li>Ensuring the security of your account and detecting potentially fraudulent activity.</li>
        </ul>
      </section>

      <section>
        <h2 id="types">3. Types of Cookies We Use</h2>

        <h3>Essential Cookies</h3>
        <p>
          These cookies are strictly necessary for the Service to function. They enable core features such as user authentication, session management, and security protections. Without these cookies, the Service cannot operate properly. Essential cookies do not require your consent.
        </p>
        <ul>
          <li><strong>Authentication tokens:</strong> Keep you signed in during your session and across visits.</li>
          <li><strong>CSRF tokens:</strong> Protect against cross-site request forgery attacks.</li>
          <li><strong>Session identifiers:</strong> Maintain your session state as you interact with the pipeline editor and other features.</li>
        </ul>

        <h3>Analytics Cookies</h3>
        <p>
          These cookies help us understand how visitors interact with the Service by collecting and reporting information anonymously. We use this data to improve the Service and identify areas where users may encounter issues. Analytics cookies are set only with your consent.
        </p>
        <ul>
          <li><strong>Page views and navigation paths:</strong> Help us understand which features are most popular and where users may encounter friction.</li>
          <li><strong>Pipeline execution metrics:</strong> Aggregated, anonymized data about pipeline usage patterns to help us optimize performance.</li>
          <li><strong>Performance monitoring:</strong> Track page load times and application responsiveness to maintain a fast experience.</li>
        </ul>

        <h3>Preference Cookies</h3>
        <p>
          These cookies allow the Service to remember choices you make, such as your preferred language, theme, or canvas layout settings, and provide a more personalized experience. Preference cookies are set only with your consent.
        </p>
        <ul>
          <li><strong>Theme preference:</strong> Remember whether you prefer light or dark mode.</li>
          <li><strong>Canvas settings:</strong> Store your preferred minimap visibility, grid snap settings, and panel layout configurations.</li>
          <li><strong>Notification preferences:</strong> Remember your choices about which notifications to display.</li>
        </ul>
      </section>

      <section>
        <h2 id="third-party">4. Third-Party Cookies</h2>
        <p>
          Some cookies on the Service are placed by third-party services that appear on our pages. We do not control the setting of these cookies. Third-party cookies we may use include:
        </p>
        <ul>
          <li><strong>Analytics providers:</strong> Such as Google Analytics or Plausible, to help us understand how visitors use the Service.</li>
          <li><strong>Payment processors:</strong> Such as Stripe, which may set cookies to facilitate secure payment transactions and fraud prevention.</li>
          <li><strong>Customer support tools:</strong> Such as Intercom or similar platforms, which may set cookies to provide you with in-app support and maintain conversation history.</li>
        </ul>
        <p>
          We encourage you to review the privacy and cookie policies of these third-party providers for more information about their practices.
        </p>
      </section>

      <section>
        <h2 id="managing">5. Managing Your Cookie Preferences</h2>
        <p>
          You have several options for managing cookies:
        </p>
        <ul>
          <li><strong>Cookie consent banner:</strong> When you first visit the Service, you will be presented with a cookie consent banner that allows you to accept or decline non-essential cookies. You can update your preferences at any time through the cookie settings link in the footer of our website.</li>
          <li><strong>Browser settings:</strong> Most web browsers allow you to control cookies through their settings. You can set your browser to refuse all cookies, accept only first-party cookies, or delete cookies when you close your browser. Note that disabling cookies may affect the functionality of the Service.</li>
          <li><strong>Opt-out tools:</strong> For analytics cookies, you can opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on. Similar opt-out mechanisms may be available for other analytics providers we use.</li>
        </ul>
        <p>
          Please note that if you choose to disable essential cookies, some parts of the Service may not function correctly or may become unavailable.
        </p>
      </section>

      <section>
        <h2 id="changes">6. Changes to This Policy</h2>
        <p>
          We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our business practices. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date above.
        </p>
        <p>
          We encourage you to review this Cookie Policy periodically. Your continued use of the Service after any modifications constitutes your acceptance of the updated policy.
        </p>
      </section>

      <section>
        <h2 id="contact">7. Contact Us</h2>
        <p>
          If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
        </p>
        <p>
          Rayloop AI, Inc.<br />
          Email: hello@rayloop.ai<br />
          Website: <a href="https://rayloop.ai">rayloop.ai</a>
        </p>
      </section>
    </LegalLayout>
  );
}
